import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';

interface HubSpotIntegrationSetupProps {
  onIntegrationCreated: (integration: any) => void;
  onCancel: () => void;
}

export const HubSpotIntegrationSetup: React.FC<HubSpotIntegrationSetupProps> = ({
  onIntegrationCreated,
  onCancel
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [authMethod, setAuthMethod] = useState<'api_key' | 'oauth'>('api_key');
  
  const [formData, setFormData] = useState({
    apiKey: '',
    portalId: '',
    clientId: '',
    clientSecret: '',
    redirectUri: '',
    scopes: ['contacts', 'timeline', 'crm.objects.contacts.read', 'crm.objects.contacts.write']
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep1 = () => {
    if (authMethod === 'api_key') {
      if (!formData.apiKey) {
        setError('API Key is required');
        return false;
      }
    } else {
      if (!formData.clientId) {
        setError('Client ID is required');
        return false;
      }
      if (!formData.clientSecret) {
        setError('Client Secret is required');
        return false;
      }
    }
    return true;
  };

  const testConnection = async () => {
    if (!validateStep1()) return;

    setLoading(true);
    setError('');
    setTestResult(null);

    try {
      // Mock connection test for now - replace with actual implementation later
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
      
      const result = {
        success: true,
        message: 'Connection successful',
        accountInfo: {
          portalId: formData.portalId || 'HubSpot Account',
          timeZone: 'America/New_York'
        }
      };
      
      setTestResult(result);
      
      if (result.success) {
        setStep(2);
      } else {
        setError(result.message || 'Connection test failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const createIntegration = async () => {
    setLoading(true);
    setError('');

    try {
      // Create a mock integration object that matches what IntegrationDashboard expects
      const mockIntegration = {
        getConfig: () => ({
          settings: {
            customSettings: {
              portalId: formData.portalId,
              authMethod: authMethod,
              apiKey: authMethod === 'api_key' ? formData.apiKey : undefined,
              clientId: authMethod === 'oauth' ? formData.clientId : undefined,
              scopes: formData.scopes
            }
          }
        })
      };

      onIntegrationCreated(mockIntegration);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create integration');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">HubSpot Connection</h3>
        <p className="text-content-secondary">
          Connect your HubSpot account to sync contacts, companies, and activities with your email marketing platform.
        </p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-300">Authentication Methods</h4>
            <p className="text-blue-800 dark:text-blue-300 text-sm mt-1">
              Choose between API Key (simpler) or OAuth (more secure):
            </p>
            <ul className="text-blue-800 dark:text-blue-300 text-sm mt-2 list-disc list-inside">
              <li><strong>API Key:</strong> Quick setup, found in HubSpot Settings → Integrations → API Key</li>
              <li><strong>OAuth:</strong> More secure, requires creating a HubSpot app</li>
            </ul>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Authentication Method
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setAuthMethod('api_key')}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              authMethod === 'api_key'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-line hover:border-line-strong'
            }`}
          >
            <h4 className="font-medium text-content">API Key</h4>
            <p className="text-sm text-content-secondary mt-1">Simple and quick setup</p>
          </button>
          <button
            type="button"
            onClick={() => setAuthMethod('oauth')}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              authMethod === 'oauth'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-line hover:border-line-strong'
            }`}
          >
            <h4 className="font-medium text-content">OAuth 2.0</h4>
            <p className="text-sm text-content-secondary mt-1">More secure, requires app setup</p>
          </button>
        </div>
      </div>

      {authMethod === 'api_key' ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              HubSpot API Key *
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={formData.apiKey}
                onChange={(e) => handleInputChange('apiKey', e.target.value)}
                placeholder="Enter your HubSpot API key"
                className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-content-subtle hover:text-content-secondary"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-content-muted mt-1">
              Found in HubSpot Settings → Integrations → API Key
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Portal ID (Optional)
            </label>
            <input
              type="text"
              value={formData.portalId}
              onChange={(e) => handleInputChange('portalId', e.target.value)}
              placeholder="Enter your HubSpot Portal ID"
              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-content-muted mt-1">
              Found in HubSpot Settings → Account Setup → Account Defaults
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-300 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 dark:text-yellow-300">OAuth Setup Required</h4>
                <p className="text-yellow-800 dark:text-yellow-300 text-sm mt-1">
                  You'll need to create a HubSpot app first:
                </p>
                <ol className="text-yellow-800 dark:text-yellow-300 text-sm mt-2 list-decimal list-inside">
                  <li>Go to HubSpot Developer Account</li>
                  <li>Create a new app</li>
                  <li>Configure OAuth settings</li>
                  <li>Add required scopes</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client ID *
              </label>
              <input
                type="text"
                value={formData.clientId}
                onChange={(e) => handleInputChange('clientId', e.target.value)}
                placeholder="Enter OAuth Client ID"
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client Secret *
              </label>
              <input
                type="password"
                value={formData.clientSecret}
                onChange={(e) => handleInputChange('clientSecret', e.target.value)}
                placeholder="Enter OAuth Client Secret"
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Redirect URI
            </label>
            <input
              type="url"
              value={formData.redirectUri}
              onChange={(e) => handleInputChange('redirectUri', e.target.value)}
              placeholder="https://your-domain.com/oauth/callback"
              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Cancel
        </button>
        <button
          onClick={testConnection}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Test Connection
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Connection Successful!</h3>
        <p className="text-content-secondary">
          Your HubSpot integration is ready to be created.
        </p>
      </div>

      {testResult && testResult.success && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
            <p className="text-green-800 dark:text-green-300 font-medium">Connected to HubSpot</p>
          </div>
          {testResult.accountInfo && (
            <div className="text-sm text-green-700 dark:text-green-300">
              <p><strong>Account:</strong> {testResult.accountInfo.portalId || 'HubSpot Account'}</p>
              {testResult.accountInfo.timeZone && (
                <p><strong>Timezone:</strong> {testResult.accountInfo.timeZone}</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900 dark:text-yellow-300">Integration Features</h4>
            <p className="text-yellow-800 dark:text-yellow-300 text-sm mt-1">
              This integration will sync:
            </p>
            <ul className="text-yellow-800 dark:text-yellow-300 text-sm mt-2 list-disc list-inside">
              <li>Contacts bidirectionally</li>
              <li>Companies and deals</li>
              <li>Email engagement to timeline</li>
              <li>Custom properties mapping</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => setStep(1)}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Back
        </button>
        <button
          onClick={createIntegration}
          disabled={loading}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Create Integration
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-content">HubSpot Integration Setup</h2>
          <div className="flex items-center gap-2 text-sm text-content-secondary">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              1
            </span>
            <span className="w-8 h-px bg-gray-300"></span>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              2
            </span>
          </div>
        </div>
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
    </div>
  );
};